import { useEffect, useState } from "react"
import { supabase } from "../../supabaseClient"
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, MenuItem, Select, Stack, TextField, Typography } from "@mui/material"
import { Add, Delete, ExpandMore } from "@mui/icons-material"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from "react-router-dom"
import { OFFICES } from "../../routes/routes"

const HierarchyRegisterView = () => {
  const navigate = useNavigate();
  const { hierarchyId } = useParams()
  const isEdit = !!hierarchyId

  const [hierarchyName, setHierarchyName] = useState('')
  const [selectedAreaId, setSelectedAreaId] = useState('')
  const [forms, setForms] = useState([])
  const [expandedForm, setExpandedForm] = useState(null)
  const [practiceAreas, setPracticeAreas] = useState([])
  const [loadingAreas, setLoadingAreas] = useState(true)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    const fetchPracticeAreas = async () => {
      try {
        const { data, error } = await supabase
          .from('practice_area')
          .select('*')
          .order('name', { ascending: true })

        if (error) throw error

        setPracticeAreas(data || [])
      } catch (err) {
        console.error('Error cargando áreas:', err)
      } finally {
        setLoadingAreas(false)
      }
    }

    fetchPracticeAreas()
  }, [])

  useEffect(() => {
    if (!isEdit) return

    const fetchHierarchy = async () => {
      try {
        setLoading(true)

        const { data, error } = await supabase
          .from('hierarchy')
          .select(`
            id,
            name,
            practice_area_id,
            hierarchy_form (
              id,
              name,
              display_order,
              case_type (
                id,
                name
              )
            )
          `)
          .eq('id', hierarchyId)
          .single()

        if (error) throw error

        setHierarchyName(data.name)
        setSelectedAreaId(data.practice_area_id)

        const mappedForms = (data.hierarchy_form || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map(f => ({
            id: f.id,
            name: f.name,
            isNew: false,
            types: (f.case_type || []).map(t => ({
              id: t.id,
              name: t.name,
              isNew: false
            }))
          }))

        setForms(mappedForms)

      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchHierarchy()
  }, [hierarchyId, isEdit])

  const addForm = () => {
    setForms([
      ...forms,
      {
        id: crypto.randomUUID(),
        name: '',
        types: [],
        isNew: true
      }
    ])
  }

  const updateFormName = (formId, value) => {
    setForms(forms.map(f => f.id === formId ? { ...f, name: value } : f))
  }

  const deleteForm = (formId) => {
    setForms(forms.filter(f => f.id !== formId))
  }

  const addType = (formId) => {
    setForms(forms.map(f => {
      if (f.id === formId) {
        return {
          ...f,
          types: [...f.types, { id: crypto.randomUUID(), name: '', isNew: true }]
        }
      }
      return f
    }))
  }

  const updateTypeName = (formId, typeId, value) => {
    setForms(forms.map(f => {
      if (f.id === formId) {
        return {
          ...f,
          types: f.types.map(t =>
            t.id === typeId ? { ...t, name: value } : t
          )
        }
      }
      return f
    }))
  }

  const deleteType = (formId, typeId) => {
    setForms(forms.map(f => {
      if (f.id === formId) {
        return {
          ...f,
          types: f.types.filter(t => t.id !== typeId)
        }
      }
      return f
    }))
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!hierarchyName || !selectedAreaId) {
        throw new Error('Nombre y área son obligatorios')
      }

      let id = hierarchyId
      if (!isEdit) {
        const { data, error } = await supabase
          .from('hierarchy')
          .insert({
            name: hierarchyName,
            practice_area_id: selectedAreaId,
            is_template: true
          })
          .select()
          .single()

        if (error) throw error
        id = data.id
      } 
      else {
        const { error } = await supabase
          .from('hierarchy')
          .update({
            name: hierarchyName,
            practice_area_id: selectedAreaId
          })
          .eq('id', id)

        if (error) throw error
      }

      for (const [order, form] of forms.entries()) {
        let formId = form.id
        if (form.isNew) {
          const { data, error } = await supabase
            .from('hierarchy_form')
            .insert({
              name: form.name,
              hierarchy_id: id,
              display_order: order + 1
            })
            .select()
            .single()

          if (error) throw error
          formId = data.id
        } else {
          await supabase
            .from('hierarchy_form')
            .update({
              name: form.name,
              display_order: order + 1
            })
            .eq('id', form.id)
        }

        for (const type of form.types) {
          if (type.isNew) {
            await supabase
              .from('case_type')
              .insert({
                name: type.name,
                hierarchy_form_id: formId
              })
          } else {
            await supabase
              .from('case_type')
              .update({ name: type.name })
              .eq('id', type.id)
          }
        }
      }

      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    setConfirmOpen(false)
    await handleDeleteHierarchy()
  }

  const handleDeleteHierarchy = async () => {
    if (!hierarchyId) return

    try {
      setLoading(true)
      setError(null)

      const { data: officeUsage, error: officeError } = await supabase
        .from('office_hierarchy')
        .select('id')
        .eq('hierarchy_id', hierarchyId)
        .limit(1)

      if (officeError) throw officeError

      if (officeUsage.length > 0) {
        throw new Error('No se puede eliminar: la jerarquía está asignada a uno o más despachos')
      }

      const { data: formsData, error: formsError } = await supabase
        .from('hierarchy_form')
        .select('id')
        .eq('hierarchy_id', hierarchyId)

      if (formsError) throw formsError

      const formIds = (formsData || []).map(f => f.id)

      if (formIds.length > 0) {

        const { data: statusData, error: statusError } = await supabase
          .from('office_status')
          .select('id')
          .in('form_id', formIds);

        if (statusError) throw statusError;

        const statusIds = (statusData || []).map(s => s.id);

        if (statusIds.length > 0) {
          const { data: caseData, error: caseError } = await supabase
            .from('case')
            .select('id')
            .in('office_status_id', statusIds)
            .limit(1);

          if (caseError) throw caseError;

          if (caseData.length > 0) {
            throw new Error(
              'No se puede eliminar: existen expedientes usando esta jerarquía'
            );
          }
        }


        if (statusIds.length > 0) {
          const { error: deleteStatusError } = await supabase
            .from('office_status')
            .delete()
            .in('id', statusIds);

          if (deleteStatusError) throw deleteStatusError;
        }

        const { data: typesData, error: typesError } = await supabase
          .from('case_type')
          .select('id')
          .in('hierarchy_form_id', formIds);

        if (typesError) throw typesError;

        const typeIds = (typesData || []).map(t => t.id);

        if (typeIds.length > 0) {
          const { error: deleteTypesError } = await supabase
            .from('case_type')
            .delete()
            .in('id', typeIds);

          if (deleteTypesError) throw deleteTypesError;
        }

        const { error: deleteFormsError } = await supabase
          .from('hierarchy_form')
          .delete()
          .in('id', formIds);

        if (deleteFormsError) throw deleteFormsError;
      }

      const { error: deleteHierarchyError } = await supabase
        .from('hierarchy')
        .delete()
        .eq('id', hierarchyId)

      if (deleteHierarchyError) throw deleteHierarchyError

      navigate(OFFICES)
    } catch (err) {
      console.error('Error eliminando jerarquía:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(OFFICES)} 
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Volver
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? 'Editar Jerarquía' : 'Crear Jerarquía'}
          </Typography>
        </Box>
        {isEdit && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setConfirmOpen(true)}
            disabled={loading}
          >
            Eliminar
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        {isEdit
          ? 'Edita la estructura de la jerarquía: Área → Forma → Tipo'
          : 'Define una jerarquía reusable: Área → Forma → Tipo'}
      </Typography>

      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Guardado correctamente</Alert>}

        <TextField
          label="Nombre de la jerarquía"
          value={hierarchyName}
          onChange={(e) => setHierarchyName(e.target.value)}
          fullWidth
        />

        <Select
          value={selectedAreaId}
          onChange={(e) => setSelectedAreaId(e.target.value)}
          fullWidth
          disabled={loadingAreas}
          displayEmpty
        >
          <MenuItem value="">
            <em>Seleccionar área</em>
          </MenuItem>
          {practiceAreas.map(area => (
            <MenuItem key={area.id} value={area.id}>
              {area.name}
            </MenuItem>
          ))}
        </Select>

        <Divider />

        <Box>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Formas</Typography>
            <Button startIcon={<Add />} onClick={addForm}>
              Nueva Forma
            </Button>
          </Box>

          {forms.length === 0 && (
            <Typography color="text.secondary">
              Aún no has creado formas. Agrega una para comenzar.
            </Typography>
          )}

          {forms.map(form => (
            <Accordion
              key={form.id}
              expanded={expandedForm === form.id}
              onChange={() =>
                setExpandedForm(expandedForm === form.id ? null : form.id)
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <TextField
                    value={form.name}
                    onChange={(e) => updateFormName(form.id, e.target.value)}
                    size="small"
                    sx={{ width: '70%' }}
                    placeholder="Nombre de la forma"
                  />
                  <IconButton onClick={() => deleteForm(form.id)}>
                    <Delete />
                  </IconButton>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Button onClick={() => addType(form.id)} startIcon={<Add />}>
                  Agregar Tipo
                </Button>

                {form.types.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Sin tipos
                  </Typography>
                )}

                {form.types.map(type => (
                  <Box key={type.id} display="flex" mt={1}>
                    <TextField
                      value={type.name}
                      onChange={(e) =>
                        updateTypeName(form.id, type.id, e.target.value)
                      }
                      size="small"
                      fullWidth
                      placeholder="Nombre del tipo"
                    />
                    <IconButton onClick={() => deleteType(form.id, type.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Eliminar jerarquía</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Esta acción eliminará la jerarquía y toda su estructura (formas, tipos y estados).
            <br /><br />
            No podrás deshacer esta acción.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default HierarchyRegisterView